import { Field, Formik } from 'formik'
import React, { type ReactNode } from 'react'

import {
  Banner,
  Button,
  Division,
  Input,
  ModalBox,
  Select,
  type SelectOption,
  useModalContext,
} from '@fourthwall/components'
import { getPrice } from '@fourthwall/utils'
import { isRequired } from '@fourthwall/utils/dist/validation'
import { PaymentMethodRequiredModal } from '@modules/Dashboard/modules/PaymentMethods'

import { useCalculateCancelRefundsQuery } from '../../queries/useCalculateCancelRefundsQuery'
import * as S from './OrderCancelConfirmModal.styled'
import type { ExpectedCostSplit, FormValues } from './OrderCancelConfirmModal.types'

export type OrderCancelConfirmModalProps = {
  orderId: string
  itemsToCancel: { variantId: string; quantity: number }[]
  content: ReactNode
  reasonOptions?: SelectOption[]
  initialReason?: string
  error?: ReactNode
  onConfirm: (expectedCostSplit?: ExpectedCostSplit, reason?: string) => void
}

export const OrderCancelConfirmModal = ({
  orderId,
  itemsToCancel,
  content,
  reasonOptions,
  initialReason,
  error,
  onConfirm,
}: OrderCancelConfirmModalProps) => {
  const { open, close } = useModalContext()
  const { calculateCancelRefundsQuery } = useCalculateCancelRefundsQuery([
    orderId,
    { itemsToCancel },
  ])

  const initialValues = {
    reason: initialReason || '',
  }

  const openPaymentMethodRequiredModal = (values: FormValues) => {
    if (
      calculateCancelRefundsQuery.data?.estimatedCost.type !== 'LOSS' ||
      calculateCancelRefundsQuery.data?.estimatedCost.chargeDetails.type !== 'REQUIRED'
    ) {
      return
    }

    const chargeDetails = calculateCancelRefundsQuery.data?.estimatedCost.chargeDetails
    const fromBalance = calculateCancelRefundsQuery.data?.estimatedCost.fromBalance
    const balancePrice = getPrice(fromBalance.value)
    const remainingPrice = getPrice(chargeDetails.amount.value)

    const balanceSplitCopy =
      fromBalance.value > 0
        ? `${balancePrice} will be deducted from your account balance and the remaining ${remainingPrice} will be charged to your payment method. `
        : ''

    open(PaymentMethodRequiredModal, {
      title: `Are you sure you want to cancel these items?`,
      subtitle: (
        <>
          <p>
            {balanceSplitCopy}Refunds are sent directly to the supporter's original payment method.
          </p>
          <p>
            <b>Payment method to cover {remainingPrice}</b>
          </p>
        </>
      ),
      submitLabel: `Confirm`,
      onSuccess: () => {
        onConfirm(
          {
            fromBalance,
            charge: chargeDetails.amount,
          },
          values.reason.length ? values.reason : undefined,
        )
      },
    })
  }

  const handleSubmit = (values: FormValues) => {
    close()
    const estimatedCost = calculateCancelRefundsQuery.data?.estimatedCost
    const zeroUSD = { value: 0, currency: 'USD' }
    const reason = values.reason.length ? values.reason : undefined

    if (!estimatedCost) {
      // That case should never happen
      onConfirm(undefined, reason)

      return
    }

    if (estimatedCost.type === 'NO_LOSS') {
      onConfirm({ fromBalance: zeroUSD, charge: zeroUSD }, reason)

      return
    }

    if (estimatedCost.type === 'LOSS') {
      const { fromBalance, chargeDetails } = estimatedCost

      if (chargeDetails.type === 'NOT_REQUIRED') {
        onConfirm({ fromBalance, charge: zeroUSD }, reason)

        return
      }

      if (chargeDetails.type === 'REQUIRED') {
        openPaymentMethodRequiredModal(values)
      }
    }
  }

  const renderInput = () => {
    if (reasonOptions) {
      return (
        <Field
          component={Select}
          name="reason"
          label="Refund reason"
          options={reasonOptions}
          validate={isRequired(
            'Please select a refund reason. This reason will appear in TikTok Seller Center.',
          )}
          suggestion="This reason will appear in TikTok Seller Center."
        />
      )
    }

    return (
      <Field
        component={Input}
        name="reason"
        label="Refund reason (optional)"
        suggestion="Only you and other managers can see this reason."
      />
    )
  }

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
      {({ submitForm }) => (
        <ModalBox
          title="Are you sure you want to cancel these items?"
          buttons={[
            <Button label="Back" appearance="secondary" onClick={close} />,
            <Button
              label="Yes, cancel these items"
              appearance="destructive"
              loading={calculateCancelRefundsQuery.isFetching}
              onClick={submitForm}
            />,
          ]}
          onClose={close}
        >
          {error && (
            <S.ErrorBannerContainer>
              <Banner appearance="danger">{error}</Banner>
            </S.ErrorBannerContainer>
          )}
          {content}
          <Division size="small">
            Once the items are canceled, this cannot be undone. The cost of refunding these items
            will be taken from your account. Refunds will be sent directly to the supporter&apos;s
            payment method (it may take up to 48 hours to process) and the items will not be
            delivered to them.
          </Division>
          <S.InputContainer>{renderInput()}</S.InputContainer>
        </ModalBox>
      )}
    </Formik>
  )
}
